import { WebIO } from '@gltf-transform/core'
import { KHRONOS_EXTENSIONS } from '@gltf-transform/extensions'
import { weld } from '@gltf-transform/functions'
import {
  addCallbacks,
  K3Extensions,
  SelectionT,
  useApp,
  useOpenInstance,
  useSetNextCamera,
} from '@k3/core'
import { Mesh, MeshStandardMaterial } from 'three'
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter'
import { USDZExporter } from 'three/examples/jsm/exporters/USDZExporter'
import { ExtraSidewallComponentInfo } from '../components/ExtraSidewallComponentInfo'

import { MontageRoot } from '../three/scene'
import { useSetConfiguratorRights } from './state/useSetConfiguratorRights'

import { CanvasProps, useThree } from '@react-three/fiber'
import packageJson from '../../package.json'
import { Helper } from '../classes/helper'
import VerandaSceneButtons from '../components/VerandaSceneButtons'
import { Background } from '../three/scene/MontageRoot'
import { MeasureContextProvider } from './state/useMeasures'
import { setNightMode } from '../store/scene/sceneSlice'
import { useVerandaDispatch } from '../'
import { screenshot } from '../three/utility/screenshot'
        
export const AR_REPLACEMENT_SOURCE_MATERIAL_NAME =
  'AR_REPLACEMENT_SOURCE_MATERIAL'
export const AR_REPLACEMENT_TARGET_MATERIAL_NAME =
  'AR_REPLACEMENT_TARGET_MATERIAL'

const handleRenderExtra = (selection: SelectionT) => {
  return <ExtraSidewallComponentInfo selection={selection} />
}

const canvasProps: Omit<CanvasProps, 'children'> = {
  frameloop: 'demand',
  className: 'canvas',
  shadows: true,
  camera: {
    far: 1000,
    fov: 40,
  },
  gl: {
    preserveDrawingBuffer: true,
  },
}

const extensions: K3Extensions = {
  app: {
    branding: {
      root: (Wrapped) => (props) => {
        return (
          <Wrapped
            {...props}
            customUrl="https://www.objectcode.de/produkte/veranda-terrassendach-konfigurator/"
            extensionName="Veranda"
            extensionVersion={packageJson.version}
          />
        )
      },
    },
    configurator: {
      root: (Wrapped) => (props) => {
        useSetConfiguratorRights()
        return <Wrapped {...props}></Wrapped>
      },
    },
    k3d: {
      canvas: {
        root: (Wrapped) => (props) => {
          const openInstance = useOpenInstance()
          const setNextCamera = useSetNextCamera()
          let camera: any = null
          const dispatch = useVerandaDispatch()

          return (
            <Wrapped
              {...props}
              canvasProps={canvasProps}
              beforeScreenshot={() => {
                dispatch(setNightMode(false))
                if (openInstance?.id !== '') {
                  setNextCamera(null)
                  camera = true
                  return null
                }
                if (!camera) {
                  return
                }

                return null
              }}
            />
          )
        },
      },
      sceneComponents: {
        K3D: {
          root: (Wrapped) => (props) => {
            return (
              <MeasureContextProvider>
                <Wrapped
                  {...props}
                  children={<MontageRoot />}
                  backgroundChildren={<Background />}
                />
              </MeasureContextProvider>
            )
          },
        },
        ContactShadows: {
          root: (Wrapped) => (props) => {
            return null
          },
        },
        OrbitControls: {
          root: (Wrapped) => (props) =>
            (
              <Wrapped
                {...props}
                makeDefault
                attach={'orbitControls'}
                maxDistance={props?.maxDistance ? props.maxDistance : 13.5}
                minDistance={3}
                rotateSpeed={0.7}
                enablePan={false}
                maxPolarAngle={Helper.convertAngleToRadiant(83)}
                minAzimuthAngle={Helper.convertAngleToRadiant(-85)}
                maxAzimuthAngle={Helper.convertAngleToRadiant(85)}
                target={[0, 1, 1]}
              />
            ),
        },
        Bounds: {
          root: (Wrapped) => (props) => {
            return (
              <Wrapped
                {...props}
                damping={props?.damping ? props.damping : 10}
                margin={1.3}
              />
            )
          },
        },
        K3Model: {
          root: (Wrapped) => (props) => {
            const app = useApp()
            const three = useThree()
            // @ts-ignore
            three.gl.shadowMap.enabled = true
            three.camera.far = 1000
            three.gl.shadowMap.needsUpdate = true
            // @ts-ignore
            if (props.model?.id === app.modelId) {
              return null
            }
            return <Wrapped {...props} />
          },
        },
      },
    },

    sceneButtons: {
      root: (Wrapped) => (props) => {
        return <VerandaSceneButtons {...props} Wrapped={Wrapped} />
      },
    },
  },
  components: {
    componentButtons: (Wrapped) => (props) =>
      <Wrapped {...props} onRenderExtra={handleRenderExtra} />,
  },
}

type MaterialReplacementMap = {
  sourceMaterial: MeshStandardMaterial
  targetMesh: Mesh
}

addCallbacks({
  setARExport: async ({ platform, scene }) => {
    const clonedScene = scene.clone(true)
    const materialsToReplaceMap: MaterialReplacementMap[] = []

    clonedScene?.traverse((child) => {
      if (
        // @ts-ignore
        child?.material?.name?.startsWith(AR_REPLACEMENT_TARGET_MATERIAL_NAME)
      ) {
        // @ts-ignore
        const id = child.material.userData?.id

        if (id) {
          const sourceObj = clonedScene.getObjectByName(
            AR_REPLACEMENT_SOURCE_MATERIAL_NAME + id
          )

          if (sourceObj) {
            // @ts-ignore
            materialsToReplaceMap.push({
              // @ts-ignore
              sourceMaterial: sourceObj.material as MeshStandardMaterial,
              // @ts-ignore
              targetMesh: child,
            })
          }
        }
      }
    })

    materialsToReplaceMap.forEach(({ sourceMaterial, targetMesh }) => {
      targetMesh.material = sourceMaterial
    })

    let arrayBuffer
    try {
      if (platform === 'AndroidOS') {
        const gltfExporter = new GLTFExporter()
        const gltf = (await gltfExporter.parseAsync(clonedScene, {
          binary: true,
          forceIndices: true,
        })) as ArrayBuffer

        const gltfUint8Array = new Uint8Array(gltf)
        const io = new WebIO().registerExtensions(KHRONOS_EXTENSIONS)
        const gltfDocument = await io.readBinary(gltfUint8Array)

        await gltfDocument.transform(weld({ tolerance: 0 }))

        arrayBuffer = await io.writeBinary(gltfDocument)
      } else if (platform === 'iOS') {
        const usdzExporter = new USDZExporter()
        arrayBuffer = await usdzExporter.parse(clonedScene)
      } else {
        throw new Error(`Unsupported AR platform type: ${platform}`)
      }
    } catch (error) {
      console.error('Fehler beim Exportieren für AR:', error)
      throw new Error(
        'Exportieren fehlgeschlagen. Bitte prüfen Sie die Szenenstruktur.'
      )
    }

    const blob = new Blob([arrayBuffer], { type: 'application/octet-stream' })
    return blob
  },
})

export const useExtensions = () => {
  return { extensions }
}
